import { App } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/services/api/client';
import * as commentsApi from '@/services/api/comments';
import * as reportsApi from '@/services/api/reports';
import * as uploadsApi from '@/services/api/uploads';
import { useAuthStore } from '@/stores/auth.store';
import type { ReportComment, ReportRecord } from '@/types/report';

export function useTeamReportDetail(reportId: string | undefined) {
  const { message } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportRecord | null>(null);
  const [comments, setComments] = useState<ReportComment[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const canComment = Boolean(
    report && currentUser && report.user.leaderId === currentUser.id,
  );

  const loadDetail = useCallback(async () => {
    if (!reportId) {
      return;
    }

    setLoading(true);
    setReport(null);
    setComments([]);
    setImageUrls({});

    try {
      const [detail, commentList] = await Promise.all([
        reportsApi.fetchReport(reportId),
        commentsApi.fetchCommentsByReport(reportId),
      ]);
      setReport(detail);
      setComments(commentList.length > 0 ? commentList : detail.comments);

      const urlEntries = await Promise.all(
        detail.images.map(async (image) => {
          try {
            const { url } = await uploadsApi.fetchReportImageAccessUrl(image.id);
            return [image.id, url] as const;
          } catch {
            return [image.id, ''] as const;
          }
        }),
      );
      setImageUrls(Object.fromEntries(urlEntries));
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载日报详情失败';
      message.error(text);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [message, reportId]);

  useEffect(() => {
    if (!reportId) {
      setReport(null);
      setComments([]);
      setImageUrls({});
      setLoading(false);
      return;
    }
    void loadDetail();
  }, [loadDetail, reportId]);

  const createComment = async (content: string) => {
    if (!reportId) {
      return;
    }

    await commentsApi.createComment({ reportId, content });
    message.success('点评已发布');
    await loadDetail();
  };

  const updateComment = async (commentId: string, content: string) => {
    await commentsApi.updateComment(commentId, { content });
    message.success('点评已更新');
    await loadDetail();
  };

  return {
    loading,
    report,
    comments,
    imageUrls,
    canComment,
    reload: loadDetail,
    createComment,
    updateComment,
  };
}
